import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { useAuth } from '../../shared/auth';

export function SettingsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings').then((r) => r.data),
  });
  const update = useMutation({
    mutationFn: ({ key, value }: { key: string; value: any }) =>
      api.put(`/settings/${key}`, value).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Settings saved');
    },
  });

  if (!data) return <div>Loading…</div>;
  const isAdmin = user?.role === 'ADMIN';

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-slate-500">
          Clinic-wide configuration. {isAdmin ? 'Admin can edit.' : 'Admin role required to edit.'}
        </p>
      </header>

      <SettingCard
        title="Clinic information"
        keyName="clinic_info"
        value={data.clinic_info}
        readOnly={!isAdmin}
        onSave={(value) => update.mutate({ key: 'clinic_info', value })}
        fields={[
          { name: 'clinicName', label: 'Clinic name' },
          { name: 'clinicAddress', label: 'Address' },
          { name: 'clinicPhone', label: 'Main phone' },
          { name: 'crisisLine', label: 'Crisis line' },
          { name: 'timezone', label: 'Timezone (IANA)' },
          {
            name: 'emrClientUrlTemplate',
            label: 'EMR client URL template — use {externalRef} as placeholder',
          },
        ]}
      />

      <SettingCard
        title="Scheduling defaults"
        keyName="scheduling_defaults"
        value={data.scheduling_defaults}
        readOnly={!isAdmin}
        onSave={(value) => update.mutate({ key: 'scheduling_defaults', value })}
        fields={[
          { name: 'instanceHorizonWeeks', label: 'Instance horizon (weeks)', type: 'number' },
          { name: 'providerUtilizationSoftCapPct', label: 'Provider util soft cap (%)', type: 'number' },
          { name: 'defaultGroupMaxClients', label: 'Default group max clients', type: 'number' },
          { name: 'enableOverrides', label: 'Allow admin overrides', type: 'boolean' },
        ]}
      />

      <SettingCard
        title="Productivity defaults"
        keyName="productivity_defaults"
        value={data.productivity_defaults}
        readOnly={!isAdmin}
        onSave={(value) => update.mutate({ key: 'productivity_defaults', value })}
        fields={[
          { name: 'therapistDirectHoursPerWeek', label: 'Therapist (hrs/wk)', type: 'number' },
          { name: 'prescriberDirectHoursPerWeek', label: 'Prescriber (hrs/wk)', type: 'number' },
          { name: 'nurseDirectHoursPerWeek', label: 'Nurse (hrs/wk)', type: 'number' },
          { name: 'communityCmDirectHoursPerWeek', label: 'Community CM (hrs/wk)', type: 'number' },
        ]}
      />

      <SettingCard
        title="Notifications & Twilio SMS"
        keyName="notification_settings"
        value={data.notification_settings ?? {
          smsEnabled: false,
          twilioAccountSid: '',
          twilioAuthToken: '',
          twilioFromNumber: '',
          missedAppointmentTemplate:
            'Talbot Behavioral Health: {client} missed {service} at {time} on {date}. Please follow up.',
        }}
        readOnly={!isAdmin}
        onSave={(value) => update.mutate({ key: 'notification_settings', value })}
        fields={[
          { name: 'smsEnabled', label: 'SMS enabled (case-manager missed-appt alerts)', type: 'boolean' },
          { name: 'twilioAccountSid', label: 'Twilio Account SID' },
          { name: 'twilioAuthToken', label: 'Twilio Auth Token', type: 'password' },
          { name: 'twilioFromNumber', label: 'Twilio sender phone (E.164, e.g. +14195550100)' },
          {
            name: 'missedAppointmentTemplate',
            label: 'Missed-appt template — placeholders: {client} {service} {time} {date}',
          },
        ]}
        helpText="When SMS is OFF or Twilio creds are blank, the system runs in dry-run mode — Notification rows are still written so you can preview who would be alerted without sending real texts."
      />

      <EmrIntegrationCard
        value={data.emr_integration ?? DEFAULT_EMR}
        readOnly={!isAdmin}
        onSave={(value) => update.mutate({ key: 'emr_integration', value })}
      />

      <SmtpSettingsCard
        value={data.smtp_settings ?? DEFAULT_SMTP}
        readOnly={!isAdmin}
        onSave={(value) => update.mutate({ key: 'smtp_settings', value })}
      />

      <HousingSyncCard
        value={data.housing_sync_settings ?? DEFAULT_HOUSING_SYNC}
        readOnly={!isAdmin}
        onSave={(value) => update.mutate({ key: 'housing_sync_settings', value })}
      />

      <GoogleSheetsSyncCard
        value={data.google_sheets_sync_settings ?? DEFAULT_GOOGLE_SHEETS_SYNC}
        readOnly={!isAdmin}
        onSave={(value) => update.mutate({ key: 'google_sheets_sync_settings', value })}
      />

      <SettingCard
        title="OIDC SSO (Microsoft 365 / Google / Okta)"
        keyName="oidc_settings"
        value={data.oidc_settings ?? DEFAULT_OIDC}
        readOnly={!isAdmin}
        onSave={(value) => update.mutate({ key: 'oidc_settings', value })}
        fields={[
          { name: 'enabled', label: 'SSO enabled', type: 'boolean' },
          {
            name: 'issuer',
            label: 'Issuer URL',
          },
          { name: 'clientId', label: 'Client ID' },
          { name: 'clientSecret', label: 'Client secret', type: 'password' },
          { name: 'redirectUri', label: 'Redirect URI (must match IdP app registration)' },
          { name: 'scopes', label: 'Scopes (space-separated)' },
          { name: 'buttonLabel', label: 'Login button label' },
          {
            name: 'allowedEmailDomains',
            label: 'Allowed email domains (comma-separated; empty = allow all)',
          },
        ]}
        helpText={
          'For Microsoft 365 the Issuer is https://login.microsoftonline.com/<TENANT-ID>/v2.0. ' +
          'Register an app in Azure Entra (App registrations), set the Redirect URI above, ' +
          'and copy the client ID + a new client secret here. New SSO users default to PROVIDER role ' +
          '(admins can promote later).'
        }
      />
    </div>
  );
}

const DEFAULT_OIDC = {
  enabled: false,
  issuer: '',
  clientId: '',
  clientSecret: '',
  // OIDC IdP redirects back to the API, not the frontend. The API's
  // /auth/oidc/callback exchanges the code, then bounces the browser to the
  // frontend `/login` page with a URL-fragment token. The default below
  // assumes API is on the same origin as the UI at port 4000 in dev; admins
  // override per their actual deployment.
  redirectUri: ((): string => {
    const apiBase = (import.meta.env.VITE_API_URL as string) || `${window.location.protocol}//${window.location.hostname}:4000`;
    return `${apiBase.replace(/\/+$/, '')}/api/v1/auth/oidc/callback`;
  })(),
  scopes: 'openid profile email',
  buttonLabel: 'Sign in with Microsoft',
  defaultRole: 'PROVIDER',
  allowedEmailDomains: '',
};

const DEFAULT_EMR = {
  enabled: false,
  baseUrl: '',
  apiKey: '',
  authHeaderName: 'Authorization',
  useBearerScheme: true,
  emrTenantId: '',
  healthCheckPath: '/health',
  timeoutSec: 15,
};

const DEFAULT_SMTP = {
  enabled: false,
  host: '',
  port: 587,
  secure: false,
  username: '',
  password: '',
  fromAddress: '',
  fromName: 'Census Scheduler',
  replyTo: '',
};

const DEFAULT_HOUSING_SYNC = {
  enabled: false,
  tenantId: '',
  clientId: '',
  clientSecret: '',
  sheetUrl: '',
  resolvedDriveId: '',
  resolvedItemId: '',
  workbookName: '',
  cronHour: 2,
  // Program writeback default ON — by explicit contract, the bedboard's
  // Program cell reflects the client's cohort for any linked row. Cohort
  // is authoritative; sheet edits get reverted on next sync.
  // Talbot ID writeback default OFF — admin opts in.
  writebackProgram: true,
  writebackTalbotId: false,
  autoCreateIntakeCandidate: false,
  lastSyncAt: null,
  lastSyncStatus: null,
  lastSyncError: null,
  detectedTabs: [] as string[],
};

const DEFAULT_GOOGLE_SHEETS_SYNC = {
  enabled: false,
  serviceAccountEmail: '',
  serviceAccountKey: '',
  sheetUrl: '',
  cronHour: 2,
  writebackProgram: true,
  writebackTalbotId: false,
  autoCreateIntakeCandidate: false,
  lastSyncAt: null,
  lastSyncStatus: null,
  lastSyncError: null,
  detectedTabs: [] as string[],
};

/**
 * EMR Integration card — a SettingCard variant with a "Test connection"
 * button. The test calls POST /settings/emr-integration/test on the server,
 * which issues a GET against `${baseUrl}${healthCheckPath}` with the
 * configured auth header and reports status/latency. The API key never
 * leaves the server; the UI only sends it via the standard Save flow.
 */
function EmrIntegrationCard({
  value,
  readOnly,
  onSave,
}: {
  value: any;
  readOnly: boolean;
  onSave: (value: any) => void;
}) {
  const [local, setLocal] = useState<any>(value);
  useEffect(() => setLocal(value), [value]);
  const test = useMutation({
    mutationFn: () => api.post('/settings/emr-integration/test').then((r) => r.data),
    onSuccess: (r: any) => {
      if (r?.ok) {
        toast.success(`EMR reachable — ${r.status} ${r.statusText ?? ''} (${r.latencyMs}ms)`);
      } else if (r?.status) {
        toast.error(`EMR responded ${r.status} ${r.statusText ?? ''} (${r.latencyMs}ms)`);
      } else {
        toast.error(r?.error ?? 'EMR test failed');
      }
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Test failed'),
  });
  return (
    <section className="card p-4">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">EMR Integration</h2>
          <p className="text-2xs text-ink-subtle">
            Outbound HTTP to an external EMR. Stored API key is masked for non-admins;
            saving <code className="rounded bg-surface-muted px-1">__REDACTED__</code> keeps the existing key intact.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!readOnly && (
            <>
              <button
                className="btn-secondary h-9"
                onClick={() => test.mutate()}
                disabled={test.isPending || !local?.baseUrl || !local?.apiKey}
                title="Issues GET ${baseUrl}${healthCheckPath} with the configured auth header"
              >
                {test.isPending ? 'Testing…' : 'Test connection'}
              </button>
              <button className="btn-primary h-9" onClick={() => onSave(local)}>
                Save
              </button>
            </>
          )}
        </div>
      </header>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Enable EMR sync">
          <label className="mt-1 flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
            <input
              type="checkbox"
              disabled={readOnly}
              checked={!!local?.enabled}
              onChange={(e) => setLocal({ ...local, enabled: e.target.checked })}
            />
            <span className="text-sm">{local?.enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        </Field>
        <Field label="Base URL" hint="No trailing slash, e.g. https://emr.atcemr.com/api/v1">
          <input
            className="input"
            disabled={readOnly}
            value={local?.baseUrl ?? ''}
            placeholder="https://emr.example.com/api/v1"
            onChange={(e) => setLocal({ ...local, baseUrl: e.target.value })}
          />
        </Field>
        <Field label="API key" hint="Stored in the AppSetting table (JSON). Redacted to non-admins on read; rely on Postgres-level encryption (e.g. AWS RDS at-rest encryption, dm-crypt volumes) for confidentiality.">
          <input
            type="password"
            className="input"
            disabled={readOnly}
            placeholder={local?.apiKey === '__REDACTED__' ? '(saved — admin only to view)' : undefined}
            value={local?.apiKey === '__REDACTED__' ? '' : (local?.apiKey ?? '')}
            onChange={(e) => setLocal({ ...local, apiKey: e.target.value })}
          />
        </Field>
        <Field label="Auth header name" hint="Most EMRs want 'Authorization' (with Bearer) or 'X-API-Key'.">
          <input
            className="input"
            disabled={readOnly}
            value={local?.authHeaderName ?? ''}
            onChange={(e) => setLocal({ ...local, authHeaderName: e.target.value })}
          />
        </Field>
        <Field label="Use Bearer scheme" hint="Sends `Bearer <key>` when on; raw key when off.">
          <label className="mt-1 flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
            <input
              type="checkbox"
              disabled={readOnly}
              checked={!!local?.useBearerScheme}
              onChange={(e) => setLocal({ ...local, useBearerScheme: e.target.checked })}
            />
            <span className="text-sm">{local?.useBearerScheme ? 'Bearer <key>' : 'Raw key'}</span>
          </label>
        </Field>
        <Field label="EMR tenant ID (optional)" hint="Sent as X-Tenant-Id header.">
          <input
            className="input"
            disabled={readOnly}
            value={local?.emrTenantId ?? ''}
            onChange={(e) => setLocal({ ...local, emrTenantId: e.target.value })}
          />
        </Field>
        <Field label="Health-check path" hint="GET'd by 'Test connection'. Defaults to /health.">
          <input
            className="input"
            disabled={readOnly}
            value={local?.healthCheckPath ?? '/health'}
            onChange={(e) => setLocal({ ...local, healthCheckPath: e.target.value })}
          />
        </Field>
        <Field label="Timeout (seconds)">
          <input
            type="number"
            className="input"
            disabled={readOnly}
            min={1}
            max={120}
            value={local?.timeoutSec ?? 15}
            onChange={(e) => setLocal({ ...local, timeoutSec: Number(e.target.value) || 15 })}
          />
        </Field>
      </div>
    </section>
  );
}

/**
 * Housing Sync card — paste Azure AD app creds + SharePoint share URL,
 * click Test to validate end-to-end against Microsoft Graph. Test mints
 * a token, resolves the URL to a driveId+itemId, and lists worksheet tabs.
 * On success, the resolved IDs + tab list are persisted server-side so the
 * eventual sync cron doesn't re-resolve every run, and so this card can
 * display "9 tabs detected: ToledoPAR1, ..." as confirmation.
 *
 * Sync engine itself (nightly cron, row matching, writeback) lands in
 * Phase 2 — this card is the Phase-1 admin surface.
 */
function HousingSyncCard({
  value,
  readOnly,
  onSave,
}: {
  value: any;
  readOnly: boolean;
  onSave: (value: any) => void;
}) {
  const [local, setLocal] = useState<any>(value);
  useEffect(() => setLocal(value), [value]);
  const test = useMutation({
    mutationFn: () => api.post('/settings/housing-sync/test').then((r) => r.data),
    onSuccess: (r: any) => {
      if (r?.ok) {
        toast.success(
          `Connected — ${r.workbookName ?? 'workbook'} (${r.tabs?.length ?? 0} tabs)`,
        );
        // Server persisted resolvedDriveId/itemId/detectedTabs; refresh local
        // so the user immediately sees them populated.
        setLocal((prev: any) => ({
          ...prev,
          resolvedDriveId: r.driveId,
          resolvedItemId: r.itemId,
          workbookName: r.workbookName,
          detectedTabs: r.tabs ?? [],
        }));
      } else {
        const at = r?.step ? ` (failed at: ${r.step})` : '';
        toast.error(`Test failed${at}: ${r?.error ?? 'unknown error'}`);
      }
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Test failed'),
  });
  return (
    <section className="card p-4">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">Housing Sync (Microsoft Graph)</h2>
          <p className="text-2xs text-ink-subtle">
            Pulls bedboard data from a shared Excel workbook on a nightly cron and pushes cohort
            changes back. Secret is masked for non-admins; saving{' '}
            <code className="rounded bg-surface-muted px-1">__REDACTED__</code> keeps the existing
            secret intact.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!readOnly && (
            <>
              <button
                className="btn-secondary h-9"
                onClick={() => test.mutate()}
                disabled={
                  test.isPending ||
                  !local?.tenantId ||
                  !local?.clientId ||
                  !local?.clientSecret ||
                  !local?.sheetUrl
                }
                title="Mints a Graph token, resolves the sheet URL, and lists worksheet tabs"
              >
                {test.isPending ? 'Testing…' : 'Test connection'}
              </button>
              <button className="btn-primary h-9" onClick={() => onSave(local)}>
                Save
              </button>
            </>
          )}
        </div>
      </header>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Enable housing sync">
          <label className="mt-1 flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
            <input
              type="checkbox"
              disabled={readOnly}
              checked={!!local?.enabled}
              onChange={(e) => setLocal({ ...local, enabled: e.target.checked })}
            />
            <span className="text-sm">{local?.enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        </Field>
        <Field label="Cron hour (clinic local, 0-23)" hint="Defaults to 2 (2 AM nightly).">
          <input
            type="number"
            className="input"
            disabled={readOnly}
            min={0}
            max={23}
            value={local?.cronHour ?? 2}
            onChange={(e) =>
              setLocal({
                ...local,
                cronHour: Math.max(0, Math.min(23, Number(e.target.value) || 2)),
              })
            }
          />
        </Field>
        <Field label="Tenant ID" hint="Azure AD tenant GUID.">
          <input
            className="input"
            disabled={readOnly}
            value={local?.tenantId ?? ''}
            placeholder="f0841574-946a-484e-bfb4-e0d2573f5de7"
            onChange={(e) => setLocal({ ...local, tenantId: e.target.value.trim() })}
          />
        </Field>
        <Field label="Client ID" hint="Azure app registration's Application (client) ID.">
          <input
            className="input"
            disabled={readOnly}
            value={local?.clientId ?? ''}
            placeholder="4f1e50ca-6a55-4a25-bec9-2493d8676301"
            onChange={(e) => setLocal({ ...local, clientId: e.target.value.trim() })}
          />
        </Field>
        <Field
          label="Client secret"
          hint="Azure app secret value. Redacted to non-admins."
        >
          <input
            type="password"
            className="input"
            disabled={readOnly}
            placeholder={
              local?.clientSecret === '__REDACTED__' ? '(saved — admin only to view)' : undefined
            }
            value={local?.clientSecret === '__REDACTED__' ? '' : (local?.clientSecret ?? '')}
            onChange={(e) => setLocal({ ...local, clientSecret: e.target.value })}
          />
        </Field>
        <Field
          label="Sheet share URL"
          hint="Paste the SharePoint share URL of the bedboard workbook verbatim."
        >
          <input
            className="input"
            disabled={readOnly}
            value={local?.sheetUrl ?? ''}
            placeholder="https://yourtenant-my.sharepoint.com/:x:/r/personal/.../BEDBOARD.xlsx?..."
            onChange={(e) => setLocal({ ...local, sheetUrl: e.target.value })}
          />
        </Field>
        <Field
          label="Push Cohort → Program column"
          hint="For linked clients only: on every sync, overwrites the bedboard's Program cell with the client's cohort label (master library name). Cohort is authoritative — operator edits on the sheet get reverted on next sync."
        >
          <label className="mt-1 flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
            <input
              type="checkbox"
              disabled={readOnly}
              checked={!!local?.writebackProgram}
              onChange={(e) => setLocal({ ...local, writebackProgram: e.target.checked })}
            />
            <span className="text-sm">{local?.writebackProgram ? 'On' : 'Off'}</span>
          </label>
        </Field>
        <Field
          label="Stamp Talbot ID column (BLANK cells only)"
          hint="Production-safe: only writes the cohortId into cells that are currently empty. Never overwrites existing operator data."
        >
          <label className="mt-1 flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
            <input
              type="checkbox"
              disabled={readOnly}
              checked={!!local?.writebackTalbotId}
              onChange={(e) => setLocal({ ...local, writebackTalbotId: e.target.checked })}
            />
            <span className="text-sm">{local?.writebackTalbotId ? 'On' : 'Off'}</span>
          </label>
        </Field>
        <Field label="Auto-create intake candidates from new rows" hint="Unmatched new rows become Pipeline candidates.">
          <label className="mt-1 flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
            <input
              type="checkbox"
              disabled={readOnly}
              checked={!!local?.autoCreateIntakeCandidate}
              onChange={(e) =>
                setLocal({ ...local, autoCreateIntakeCandidate: e.target.checked })
              }
            />
            <span className="text-sm">{local?.autoCreateIntakeCandidate ? 'On' : 'Off'}</span>
          </label>
        </Field>
      </div>

      {(local?.workbookName || (local?.detectedTabs ?? []).length > 0) && (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50/40 p-3 text-xs">
          <div className="font-semibold text-emerald-800">
            ✓ Connected workbook: {local.workbookName || '(unnamed)'}
          </div>
          <div className="mt-1 text-ink-subtle">
            driveId: <code>{(local.resolvedDriveId || '').slice(0, 24)}…</code> · itemId:{' '}
            <code>{local.resolvedItemId || '—'}</code>
          </div>
          {(local.detectedTabs ?? []).length > 0 && (
            <div className="mt-2">
              <span className="text-ink-subtle">Tabs detected:</span>{' '}
              {local.detectedTabs.map((t: string) => (
                <span
                  key={t}
                  className="mr-1 inline-block rounded bg-white px-1.5 py-0.5 text-2xs ring-1 ring-emerald-200"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {local?.lastSyncAt && (
        <div className="mt-3 rounded-md border border-line-subtle bg-surface-subtle/60 p-2 text-xs">
          Last sync: {local.lastSyncAt}{' '}
          <span
            className={
              local.lastSyncStatus === 'OK'
                ? 'pill-success'
                : local.lastSyncStatus === 'FAILED'
                  ? 'pill-danger'
                  : 'pill-muted'
            }
          >
            {local.lastSyncStatus ?? '—'}
          </span>
          {local.lastSyncError && (
            <div className="mt-1 text-rose-700">{local.lastSyncError}</div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Housing Sync (Google Sheets) — the Google counterpart to HousingSyncCard.
 * Separate settings record (google_sheets_sync_settings) and a separate
 * service-account credential shape. The Microsoft Graph card is untouched.
 */
function GoogleSheetsSyncCard({
  value,
  readOnly,
  onSave,
}: {
  value: any;
  readOnly: boolean;
  onSave: (value: any) => void;
}) {
  const [local, setLocal] = useState<any>(value);
  useEffect(() => setLocal(value), [value]);
  const test = useMutation({
    mutationFn: () => api.post('/housing/google/test').then((r) => r.data),
    onSuccess: (r: any) => {
      if (r?.ok) {
        toast.success(`Connected — ${r.tabs?.length ?? 0} tabs`);
        // Server persisted detectedTabs; refresh local so they show immediately.
        setLocal((prev: any) => ({ ...prev, detectedTabs: r.tabs ?? [] }));
      } else {
        toast.error(`Test failed: ${r?.error ?? 'unknown error'}`);
      }
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Test failed'),
  });
  return (
    <section className="card p-4">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">Housing Sync (Google Sheets)</h2>
          <p className="text-2xs text-ink-subtle">
            Pulls bedboard data from a Google Sheet on a nightly cron and pushes cohort changes
            back, using a service account. The key is masked for non-admins; saving{' '}
            <code className="rounded bg-surface-muted px-1">__REDACTED__</code> keeps the existing
            key intact.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!readOnly && (
            <>
              <button
                className="btn-secondary h-9"
                onClick={() => test.mutate()}
                disabled={
                  test.isPending ||
                  !local?.serviceAccountEmail ||
                  !local?.serviceAccountKey ||
                  !local?.sheetUrl
                }
                title="Mints a service-account token and lists the spreadsheet's tabs. Save first — Test reads the stored settings."
              >
                {test.isPending ? 'Testing…' : 'Test connection'}
              </button>
              <button className="btn-primary h-9" onClick={() => onSave(local)}>
                Save
              </button>
            </>
          )}
        </div>
      </header>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Enable Google housing sync">
          <label className="mt-1 flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
            <input
              type="checkbox"
              disabled={readOnly}
              checked={!!local?.enabled}
              onChange={(e) => setLocal({ ...local, enabled: e.target.checked })}
            />
            <span className="text-sm">{local?.enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        </Field>
        <Field label="Cron hour (clinic local, 0-23)" hint="Defaults to 2 (2 AM nightly).">
          <input
            type="number"
            className="input"
            disabled={readOnly}
            min={0}
            max={23}
            value={local?.cronHour ?? 2}
            onChange={(e) =>
              setLocal({
                ...local,
                cronHour: Math.max(0, Math.min(23, Number(e.target.value) || 2)),
              })
            }
          />
        </Field>
        <Field label="Service account email" hint="The xxx@project.iam.gserviceaccount.com address. Share the sheet with it.">
          <input
            className="input"
            disabled={readOnly}
            value={local?.serviceAccountEmail ?? ''}
            placeholder="census-housing@project-123.iam.gserviceaccount.com"
            onChange={(e) => setLocal({ ...local, serviceAccountEmail: e.target.value.trim() })}
          />
        </Field>
        <Field label="Sheet URL" hint="Paste the Google Sheets URL of the bedboard workbook.">
          <input
            className="input"
            disabled={readOnly}
            value={local?.sheetUrl ?? ''}
            placeholder="https://docs.google.com/spreadsheets/d/…/edit"
            onChange={(e) => setLocal({ ...local, sheetUrl: e.target.value })}
          />
        </Field>
        <div className="sm:col-span-2">
          <Field
            label="Service account private key (PEM)"
            hint="The full -----BEGIN PRIVATE KEY----- block from the service-account JSON. Masked for non-admins."
          >
            <textarea
              className="input min-h-[88px] font-mono text-2xs"
              disabled={readOnly}
              placeholder={
                local?.serviceAccountKey === '__REDACTED__'
                  ? '(saved — admin only to view)'
                  : '-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----'
              }
              value={local?.serviceAccountKey === '__REDACTED__' ? '' : (local?.serviceAccountKey ?? '')}
              onChange={(e) => setLocal({ ...local, serviceAccountKey: e.target.value })}
            />
          </Field>
        </div>
        <Field
          label="Push Cohort → Program column"
          hint="For linked clients only: on every sync, overwrites the bedboard's Program cell with the client's cohort label (master library name). Cohort is authoritative — operator edits on the sheet get reverted on next sync."
        >
          <label className="mt-1 flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
            <input
              type="checkbox"
              disabled={readOnly}
              checked={!!local?.writebackProgram}
              onChange={(e) => setLocal({ ...local, writebackProgram: e.target.checked })}
            />
            <span className="text-sm">{local?.writebackProgram ? 'On' : 'Off'}</span>
          </label>
        </Field>
        <Field
          label="Stamp Talbot ID column (BLANK cells only)"
          hint="Production-safe: only writes the cohortId into cells that are currently empty. Never overwrites existing operator data."
        >
          <label className="mt-1 flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
            <input
              type="checkbox"
              disabled={readOnly}
              checked={!!local?.writebackTalbotId}
              onChange={(e) => setLocal({ ...local, writebackTalbotId: e.target.checked })}
            />
            <span className="text-sm">{local?.writebackTalbotId ? 'On' : 'Off'}</span>
          </label>
        </Field>
      </div>

      {(local?.detectedTabs ?? []).length > 0 && (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50/40 p-3 text-xs">
          <div className="font-semibold text-emerald-800">✓ Connected — tabs detected</div>
          <div className="mt-2">
            {local.detectedTabs.map((t: string) => (
              <span
                key={t}
                className="mr-1 inline-block rounded bg-white px-1.5 py-0.5 text-2xs ring-1 ring-emerald-200"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {local?.lastSyncAt && (
        <div className="mt-3 rounded-md border border-line-subtle bg-surface-subtle/60 p-2 text-xs">
          Last sync: {local.lastSyncAt}{' '}
          <span
            className={
              local.lastSyncStatus === 'OK'
                ? 'pill-success'
                : local.lastSyncStatus === 'FAILED'
                  ? 'pill-danger'
                  : 'pill-muted'
            }
          >
            {local.lastSyncStatus ?? '—'}
          </span>
          {local.lastSyncError && (
            <div className="mt-1 text-rose-700">{local.lastSyncError}</div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * SMTP card — same redaction pattern as the EMR card (password field is
 * masked for non-admins; saving `__REDACTED__` keeps the existing password).
 * "Send test" calls POST /scheduled-reports/test-smtp with a recipient and
 * shows the result (SENT / DRY_RUN / FAILED) inline so the admin can verify
 * the config end-to-end before they configure any scheduled reports.
 */
function SmtpSettingsCard({
  value,
  readOnly,
  onSave,
}: {
  value: any;
  readOnly: boolean;
  onSave: (value: any) => void;
}) {
  const [local, setLocal] = useState<any>(value);
  const [testTo, setTestTo] = useState<string>('');
  useEffect(() => setLocal(value), [value]);
  const test = useMutation({
    mutationFn: (to: string) =>
      api.post('/scheduled-reports/test-smtp', { to }).then((r) => r.data),
    onSuccess: (r: any) => {
      if (r?.status === 'SENT') {
        toast.success(`Test email sent (id ${r.messageId ?? '—'})`);
      } else if (r?.status === 'DRY_RUN') {
        toast.message(`SMTP dry-run: ${r.error ?? 'disabled'}`);
      } else {
        toast.error(r?.error ?? 'SMTP test failed');
      }
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Test failed'),
  });
  return (
    <section className="card p-4">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">SMTP (outbound email)</h2>
          <p className="text-2xs text-ink-subtle">
            Used by the SMTP test below and by the Scheduled Reports runner.
            When disabled, the system enters dry-run — reports render but no email is sent.
          </p>
        </div>
        {!readOnly && (
          <button className="btn-primary h-9" onClick={() => onSave(local)}>
            Save
          </button>
        )}
      </header>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="SMTP enabled">
          <label className="mt-1 flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
            <input
              type="checkbox"
              disabled={readOnly}
              checked={!!local?.enabled}
              onChange={(e) => setLocal({ ...local, enabled: e.target.checked })}
            />
            <span className="text-sm">{local?.enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        </Field>
        <Field label="Host" hint="e.g. smtp.office365.com, smtp.gmail.com, smtp.sendgrid.net">
          <input
            className="input"
            disabled={readOnly}
            value={local?.host ?? ''}
            placeholder="smtp.office365.com"
            onChange={(e) => setLocal({ ...local, host: e.target.value })}
          />
        </Field>
        <Field label="Port" hint="587 for STARTTLS · 465 for implicit TLS">
          <input
            type="number"
            className="input"
            disabled={readOnly}
            min={1}
            max={65535}
            value={local?.port ?? 587}
            onChange={(e) => setLocal({ ...local, port: Number(e.target.value) || 587 })}
          />
        </Field>
        <Field label="Implicit TLS (secure)" hint="ON for port 465; OFF for 587 / STARTTLS">
          <label className="mt-1 flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
            <input
              type="checkbox"
              disabled={readOnly}
              checked={!!local?.secure}
              onChange={(e) => setLocal({ ...local, secure: e.target.checked })}
            />
            <span className="text-sm">{local?.secure ? 'secure=true' : 'secure=false'}</span>
          </label>
        </Field>
        <Field label="Username" hint="Mailbox address for Office 365 / Gmail; 'apikey' for SendGrid.">
          <input
            className="input"
            disabled={readOnly}
            value={local?.username ?? ''}
            onChange={(e) => setLocal({ ...local, username: e.target.value })}
          />
        </Field>
        <Field label="Password / app password / API token" hint="Redacted for non-admins.">
          <input
            type="password"
            className="input"
            disabled={readOnly}
            placeholder={local?.password === '__REDACTED__' ? '(saved — admin only to view)' : undefined}
            value={local?.password === '__REDACTED__' ? '' : (local?.password ?? '')}
            onChange={(e) => setLocal({ ...local, password: e.target.value })}
          />
        </Field>
        <Field label="From address" hint="e.g. reports@yourclinic.com — must be authorised to send by your provider.">
          <input
            className="input"
            disabled={readOnly}
            value={local?.fromAddress ?? ''}
            placeholder="reports@yourclinic.com"
            onChange={(e) => setLocal({ ...local, fromAddress: e.target.value })}
          />
        </Field>
        <Field label="From name">
          <input
            className="input"
            disabled={readOnly}
            value={local?.fromName ?? ''}
            onChange={(e) => setLocal({ ...local, fromName: e.target.value })}
          />
        </Field>
        <Field label="Reply-to (optional)">
          <input
            className="input"
            disabled={readOnly}
            value={local?.replyTo ?? ''}
            onChange={(e) => setLocal({ ...local, replyTo: e.target.value })}
          />
        </Field>
      </div>

      {!readOnly && (
        <div className="mt-4 flex flex-wrap items-end gap-2 rounded border border-line-subtle bg-surface-subtle/60 p-3">
          <Field label="Send test to">
            <input
              type="email"
              className="input"
              placeholder="you@yourclinic.com"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
          </Field>
          <button
            className="btn-secondary h-9"
            disabled={!testTo || test.isPending}
            onClick={() => test.mutate(testTo)}
            title="Sends a one-off test email using the current settings. Save first if you changed any fields."
          >
            {test.isPending ? 'Sending…' : 'Send test email'}
          </button>
          <p className="ml-2 text-2xs text-ink-subtle">
            Save your changes before testing so the server uses the latest values.
          </p>
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {hint && <p className="mt-1 text-2xs text-ink-subtle">{hint}</p>}
    </div>
  );
}

interface Field {
  name: string;
  label: string;
  type?: 'string' | 'number' | 'boolean' | 'password';
}

function SettingCard({
  title,
  value,
  readOnly,
  onSave,
  fields,
  helpText,
}: {
  title: string;
  keyName: string;
  value: any;
  readOnly: boolean;
  onSave: (value: any) => void;
  fields: Field[];
  helpText?: string;
}) {
  const [local, setLocal] = useState<any>(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <section className="card p-4">
      <header className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold">{title}</h2>
        {!readOnly && (
          <button className="btn-primary" onClick={() => onSave(local)}>
            Save
          </button>
        )}
      </header>
      {helpText && (
        <p className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          {helpText}
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map((f) => (
          <div key={f.name}>
            <label className="label">{f.label}</label>
            {f.type === 'boolean' ? (
              <label className="mt-1 flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
                <input
                  type="checkbox"
                  disabled={readOnly}
                  checked={!!local?.[f.name]}
                  onChange={(e) => setLocal({ ...local, [f.name]: e.target.checked })}
                />
                <span className="text-sm">{local?.[f.name] ? 'Enabled' : 'Disabled'}</span>
              </label>
            ) : (
              <input
                className="input"
                disabled={readOnly}
                type={
                  f.type === 'number' ? 'number' : f.type === 'password' ? 'password' : 'text'
                }
                placeholder={
                  local?.[f.name] === '__REDACTED__' ? '(saved — admin only to view)' : undefined
                }
                value={local?.[f.name] === '__REDACTED__' ? '' : (local?.[f.name] ?? '')}
                onChange={(e) =>
                  setLocal({
                    ...local,
                    [f.name]: f.type === 'number' ? Number(e.target.value) : e.target.value,
                  })
                }
              />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
